import { useCallback, useEffect, useState } from 'react'
import { Loader2, Lock, Plus, ShieldCheck, Trash2 } from 'lucide-react'
import { opcoApi } from '../../../services/api'
import { useToast } from '../../../context/ToastContext'
import { ConfirmDialog, PanelState, asList, cardCls, errorMessage, inputCls, primaryBtn } from './shared'
import SuperAdminDefinition from './SuperAdminDefinition'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function validateAdminEmail(value) {
  if (!value) return 'Enter an email address'
  return EMAIL_RE.test(value) ? null : 'Enter a full email address, e.g. name@example.com'
}

// Who is a Super Admin. Admins named in the deployment settings
// (OPCO_ADMIN_EMAILS) are permanent and can't be removed here; any admin can add
// or remove the rest. The person must already have an account in this organization
// and the Admin role: a listed person without it gets no Super Admin rights, and
// the row says so (the server sends has_admin_role / effective per row).
function needsAdminRole(admin) {
  return Boolean(admin?.user_id) && admin.is_active !== false && admin.effective === false
}

export default function AdminsPanel() {
  const toast = useToast()
  const [admins, setAdmins] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [email, setEmail] = useState('')
  const [touched, setTouched] = useState(false)
  const [saving, setSaving] = useState(false)
  const [removing, setRemoving] = useState(null)
  const [removeBusy, setRemoveBusy] = useState(false)
  const [granting, setGranting] = useState(false) // confirm dialog before the role is given

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await opcoApi.listAdmins()
      setAdmins(asList(res?.data, 'admins'))
    } catch (err) {
      setError(errorMessage(err, 'Failed to load Super Admins'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const normalized = email.trim().toLowerCase()
  const validation = validateAdminEmail(normalized)

  // Validate, then show what the role gives before anything is granted.
  const ask = (e) => {
    e.preventDefault()
    setTouched(true)
    if (validation) return
    setGranting(true)
  }

  const add = async () => {
    setSaving(true)
    try {
      const res = await opcoApi.grantAdmin(normalized)
      if (res?.data?.already) {
        toast.success('Already an admin', `${normalized} is already a Super Admin`)
      } else {
        toast.success('Super Admin added', `${normalized} can now manage OpCos`)
      }
      setEmail('')
      setTouched(false)
      await load()
    } catch (err) {
      toast.error('Could not add admin', errorMessage(err))
    } finally {
      setSaving(false)
      setGranting(false)
    }
  }

  const confirmRemove = async () => {
    setRemoveBusy(true)
    try {
      await opcoApi.revokeAdmin(removing.user_id)
      toast.success('Super Admin removed', `${removing.email} is no longer a Super Admin`)
      setRemoving(null)
      await load()
    } catch (err) {
      toast.error('Could not remove admin', errorMessage(err))
      setRemoving(null)
    } finally {
      setRemoveBusy(false)
    }
  }

  return (
    <section className={cardCls} aria-label="Super Admins">
      <div className="border-b border-gray-100 dark:border-gray-800 px-5 py-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-gray-400" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Super Admins</h2>
        </div>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Read what the role allows before you add anyone.
        </p>
        <SuperAdminDefinition className="mt-3" />
      </div>

      <form onSubmit={ask} className="flex flex-wrap items-start gap-2 px-5 py-4" noValidate>
        <div className="min-w-[12rem] flex-1">
          <input
            type="email"
            aria-label="Admin email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@example.com"
            className={inputCls}
            aria-invalid={Boolean(touched && validation)}
          />
          {touched && validation && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{validation}</p>}
        </div>
        <button type="submit" disabled={saving} className={primaryBtn}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Make admin
        </button>
      </form>

      <PanelState loading={loading} error={error} onRetry={load} empty={admins.length === 0} emptyText="No Super Admins.">
        <ul className="divide-y divide-gray-100 dark:divide-gray-800 border-t border-gray-100 dark:border-gray-800">
          {admins.map((a) => (
            <li key={a.user_id || a.email} className="flex items-center gap-3 px-5 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-gray-900 dark:text-gray-100">{a.username || a.email}</p>
                {a.username && a.username !== a.email && (
                  <p className="truncate text-xs text-gray-500 dark:text-gray-400">{a.email}</p>
                )}
                {a.is_active === false && (
                  <p className="text-xs font-semibold text-amber-600 dark:text-amber-400">
                    Deactivated: has no access until reactivated
                  </p>
                )}
                {needsAdminRole(a) && (
                  <p className="text-xs font-semibold text-amber-600 dark:text-amber-400">
                    Not active: needs the Admin role
                  </p>
                )}
              </div>
              {a.source === 'environment' ? (
                <span
                  title="Set in the deployment settings (OPCO_ADMIN_EMAILS). Whoever deploys the app has to change it."
                  className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400"
                >
                  <Lock className="h-3.5 w-3.5" /> Permanent
                </span>
              ) : (
                <>
                  {a.granted_by_email && (
                    <span className="hidden sm:inline truncate text-xs text-gray-500 dark:text-gray-400">
                      Added by {a.granted_by_email}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => setRemoving(a)}
                    aria-label={`Remove ${a.email} as Super Admin`}
                    className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      </PanelState>

      {granting && (
        <ConfirmDialog
          title="Make Super Admin"
          confirmLabel="Make Super Admin"
          busy={saving}
          onConfirm={add}
          onCancel={() => setGranting(false)}
          message={`${normalized} will be able to:`}
        >
          <SuperAdminDefinition compact className="!border-0 !bg-transparent !p-0" />
        </ConfirmDialog>
      )}

      {removing && (
        <ConfirmDialog
          title="Remove Super Admin"
          message={`${removing.email} will lose the All OpCos view and Super Admin rights. Their account and OpCo memberships are kept.`}
          confirmLabel="Remove"
          danger
          busy={removeBusy}
          onConfirm={confirmRemove}
          onCancel={() => setRemoving(null)}
        />
      )}
    </section>
  )
}
